<template>
  <div v-if="user" class="ui-panel">
    <div class="ui-panel__head">
      <ui-avatar
        :text="memberInitials(user)"
        :tone="isCourier(user) ? 'accent' : 'blue'"
        big
      />
      <div>
        <small>{{ user.email }}</small>
      </div>
    </div>

    <div class="ui-tags">
      <ui-chip :tone="isCourier(user) ? 'plain' : 'blue'">{{ roleLabel(user) }}</ui-chip>
      <team-status :user="user" />
    </div>

    <ui-banner v-if="user.suspended" icon="mdi-cancel">{{
      user.reason
        ? $t("delivery_team.panel.suspended", { reason: toLatin(user.reason) })
        : $t("delivery_team.panel.suspended_no_reason")
    }}</ui-banner>

    <ui-banner v-if="missing" tone="warn">
      {{ $t("delivery_team.panel.missing", { items: missing }) }}
      <button type="button" class="ui-link" @click="shell.openForm(user.id)">
        {{ $t("delivery_team.panel.complete") }}
      </button>
    </ui-banner>

    <ui-confirm
      v-if="confirming"
      :title="$t('delivery_team.panel.confirm_title', { name })"
    >
      {{ $t("delivery_team.panel.confirm_text") }}
      <template #actions>
        <ui-button variant="dark" size="sm" :disabled="busy" @click="deactivate">{{
          $t("delivery_team.panel.deactivate")
        }}</ui-button>
        <ui-button size="sm" @click="confirming = false">{{ $t("cancel") }}</ui-button>
      </template>
    </ui-confirm>

    <div class="ui-actions">
      <ui-button
        variant="primary"
        icon="mdi-pencil-outline"
        @click="shell.openForm(user.id)"
        >{{ $t("delivery_team.panel.edit") }}</ui-button
      >
      <ui-button icon="mdi-qrcode-scan" @click="shell.openQr(user)">{{
        $t("delivery_team.panel.qr")
      }}</ui-button>
      <ui-button
        v-if="user.active && !confirming"
        icon="mdi-power"
        @click="confirming = true"
        >{{ $t("delivery_team.panel.deactivate") }}</ui-button
      >
    </div>

    <ui-section :title="$t('delivery_team.sections.contact')">
      <ui-kv :rows="contactRows" :empty-text="$t('delivery_team.panel.not_entered')" />
    </ui-section>

    <template v-if="isCourier(user)">
      <ui-section :title="$t('delivery_team.sections.payment')">
        <ui-kv :rows="paymentRows" :empty-text="$t('delivery_team.panel.not_entered')" />
      </ui-section>
      <ui-section :title="$t('delivery_team.sections.personal')">
        <ui-kv :rows="personalRows" :empty-text="$t('delivery_team.panel.not_entered')" />
      </ui-section>
      <ui-section :title="$t('delivery_team.sections.referrals')">
        <ui-kv :rows="referralRows">
          <template #value="{ row }">
            <button
              v-if="row.user"
              type="button"
              class="ui-link"
              @click="shell.openUser(row.user.id)"
            >
              #{{ row.user.id }} {{ memberName(row.user) }}
            </button>
            <template v-else>{{
              row.value || $t("delivery_team.panel.not_entered")
            }}</template>
          </template>
        </ui-kv>
      </ui-section>
    </template>
    <ui-banner v-else tone="info">{{
      $t("delivery_team.panel.dispatcher_note")
    }}</ui-banner>
  </div>
</template>

<script>
import hasTeamLabels from "../hasTeamLabels";
import TeamStatus from "../parts/TeamStatus.vue";

/** Kartica korisnika u desnom panelu: status, šta nedostaje, radnje i sve što backend zna o osobi i ugovoru. */
export default {
  name: "TeamMemberPanel",
  components: { TeamStatus },
  mixins: [hasTeamLabels],
  inject: ["reportShell"],
  props: { userId: { type: Number, required: true } },
  data: () => ({ confirming: false, busy: false }),
  computed: {
    shell() {
      return this.reportShell;
    },
    user() {
      return this.shell.users.find((item) => item.id === this.userId) || null;
    },
    name() {
      return this.user ? this.memberName(this.user) : "";
    },
    missing() {
      return this.user ? this.missingText(this.user) : "";
    },
    contactRows() {
      return [
        { label: this.$t("phone"), value: this.user.phone },
        { label: this.$t("email_or_username"), value: this.user.email },
      ];
    },
    paymentRows() {
      const u = this.user;
      return [
        { label: this.$t("courier_pay_type"), value: this.payTypeLabel(u.ptype) },
        { label: this.$t("amount"), value: this.payAmountText(u, this.shell.currency) },
        { label: this.$t("bank_account"), value: u.bank },
        { label: this.$t("contract_signed_at"), value: this.dateText(u.signed) },
        { label: this.$t("contract_active_from"), value: this.dateText(u.from) },
        { label: this.$t("note"), value: u.note },
      ];
    },
    personalRows() {
      const u = this.user;
      return [
        { label: this.$t("date_of_birth"), value: this.dateText(u.dob) },
        { label: "IBAN", value: u.iban },
        {
          label: this.$t("emergency_contact_name"),
          value: [u.ecn, u.ecp].filter(Boolean).join(" · "),
        },
      ];
    },
    referralRows() {
      const u = this.user;
      const referrer = u.rby
        ? this.shell.users.find((item) => String(item.id) === String(u.rby))
        : null;
      return [
        { label: this.$t("referral_url"), value: u.rurl },
        { label: this.$t("referral_short_url"), value: u.rshort },
        {
          label: this.$t("referred_by"),
          value: u.rby ? `#${u.rby}` : "",
          user: referrer,
        },
      ];
    },
  },
  watch: {
    userId() {
      this.confirming = false;
    },
  },
  methods: {
    async deactivate() {
      this.busy = true;
      try {
        await this.shell.deactivateMember(this.user);
        this.confirming = false;
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
